import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { ConsultationPhoto } from '../../types/domain';
import { prepareConsultationImage } from '../../lib/imagePrep';
import ConsultationPhotoGallery from '../components/ConsultationPhotoGallery';

/** Enough for one visit's paperwork; more is almost certainly a mis-pick. */
export const MAX_PHOTOS_PER_UPLOAD = 10;

const todayLocal = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

/**
 * FR-17 — consultation-result photos (SoW Bagian 10, cut by A4).
 *
 * Upload and look back. There is deliberately nothing else here: no caption
 * thread, no reply, no "send to doctor" — A4 removed messaging, and a text box
 * next to a photo is where it would creep back in.
 */
export default function ConsultationPhotosPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [enabled, setEnabled] = useState(true);
  const [photos, setPhotos] = useState<ConsultationPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayLocal());
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      return;
    }
    setLoading(true);
    try {
      setPhotos(await dataService.listConsultationPhotos(user.id));
    } catch (err) {
      console.error(err);
      setError('Gagal memuat foto. Periksa koneksi internet.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
    // Fallback true: a switch that cannot be read (offline) must not hide the
    // screen; the upload itself fails loudly without a connection anyway.
    dataService.getAppSetting('consultation_photos_enabled', true).then(v => setEnabled(v !== false));
  }, [load]);

  const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setNotice(null);
    const picked = Array.from(event.target.files ?? []);
    if (picked.length > MAX_PHOTOS_PER_UPLOAD) {
      setError(`Maksimal ${MAX_PHOTOS_PER_UPLOAD} foto sekali unggah.`);
      setFiles([]);
      return;
    }
    setFiles(picked);
  };

  const handleUpload = async () => {
    if (!user || files.length === 0) {
      return;
    }
    setError(null);
    setNotice(null);
    let done = 0;
    try {
      for (const file of files) {
        setProgress(`Mengunggah ${done + 1} dari ${files.length}...`);
        const image = await prepareConsultationImage(file);
        await dataService.uploadConsultationPhoto(user.id, image, date);
        done += 1;
      }
      setNotice(`${done} foto terunggah.`);
      setFiles([]);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    } catch (err: any) {
      // Say how many made it: a parent re-picking all five after the third
      // failed would upload the first two twice.
      const message = err?.message || 'Gagal mengunggah foto.';
      setError(done > 0 ? `${done} foto terunggah, sisanya gagal: ${message}` : message);
    } finally {
      setProgress(null);
      await load();
    }
  };

  const handleDelete = async (photo: ConsultationPhoto) => {
    setError(null);
    setNotice(null);
    try {
      await dataService.deleteConsultationPhoto(photo);
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
    } catch (err: any) {
      setError(err?.message || 'Gagal menghapus foto.');
    }
  };

  const busy = progress !== null;

  return (
    <div className="screen min-h-[100dvh] h-full">
      <section className="screen-card" aria-labelledby="photos-title">
        <div id="photos-title" className="section-title">
          Foto konsultasi
        </div>
        <div className="subtitle">
          Simpan foto hasil konsultasi, misalnya hasil lab atau catatan dokter. Dokter kamu
          bisa melihatnya juga.
        </div>

        {enabled ? (
          <div className="clinical-form">
            <div className="field-group">
              <div className="field">
                <label htmlFor="consultation-date">Tanggal konsultasi</label>
                <input
                  id="consultation-date"
                  type="date"
                  value={date}
                  max={todayLocal()}
                  disabled={busy}
                  onChange={e => setDate(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="consultation-files">Pilih foto</label>
                <input
                  id="consultation-files"
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={busy}
                  onChange={handlePick}
                />
              </div>
            </div>

            {files.length > 0 ? (
              <div className="subtitle">{files.length} foto dipilih.</div>
            ) : null}
            {progress ? (
              <div className="target-band-preview" role="status">
                {progress}
              </div>
            ) : null}
            {error ? <div className="target-band-error">{error}</div> : null}
            {notice ? <div className="target-band-preview">{notice}</div> : null}

            <div className="target-actions">
              <button
                type="button"
                className="btn-primary"
                disabled={busy || files.length === 0}
                onClick={handleUpload}
              >
                {busy ? 'Mengunggah...' : 'Unggah foto'}
              </button>
            </div>
          </div>
        ) : (
          <div className="profile-nudge" role="status">
            Unggah foto konsultasi sedang dinonaktifkan oleh admin.
          </div>
        )}
      </section>

      <section className="screen-card" aria-label="Daftar foto konsultasi">
        {loading ? (
          <div className="subtitle">Memuat foto...</div>
        ) : (
          <ConsultationPhotoGallery photos={photos} onDelete={handleDelete} />
        )}
      </section>

      <div className="next-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
          Kembali
        </button>
      </div>
    </div>
  );
}
